import { ConfigProvider, theme } from 'antd';
import { getThemeMode } from './theme-mode';

const FONT_FAMILY = "Manrope, 'Segoe UI', system-ui, -apple-system, sans-serif";

/**
 * antd tema yapılandırması — TEK kaynak (App ve statik API'ler aynı nesneyi kullanır).
 *
 * v4'teki antd.compact.css'in v5 karşılığı: yoğunluk artık CSS değil tema algoritması.
 * Renkler marka token'larından (core/style/brand.css) gelir — antd'nin fabrika mavisi
 * (#1890ff) yerine slate birincil + amber uyarı. Böylece buton/link/seçim rengi header
 * ile aynı dili konuşur.
 *
 * İki mod var (bkz. theme-mode.js): açık (varsayılan, aşağıdaki antdTheme) ve koyu (antdDarkTheme).
 */
export const antdTheme = {
    algorithm: theme.compactAlgorithm,
    token: {
        colorPrimary: '#34495E',
        colorLink: '#34495E',
        colorLinkHover: '#44607C',
        colorWarning: '#E0982A',
        // SEÇİLİ item zemini (dropdown/select option, date picker, cascader, menü) — antd
        // bunu colorPrimary'den türetirdi ve slate ile grimsi-mavi bir ton çıkıyordu.
        // Seçim de marka amber'i: grid'deki seçili satırla (Table.rowSelectedBg) aynı zemin.
        controlItemBgActive: '#F5DEB9',        // = --accent-row
        controlItemBgActiveHover: '#EFD3A8',
        // .ant-layout kendi zeminini basar (v5 varsayılanı #f5f5f5) ve body'yi örter
        // → marka nötrüne bağla, yoksa iki farklı gri yan yana görünür (bkz. --bg)
        colorBgLayout: '#F6F7F9',
        colorText: '#2C3E50',
        borderRadius: 6,
        fontFamily: FONT_FAMILY,
    },
    components: {
        // antd seçili satır zeminini colorPrimary'den türetiyor; slate primary ile
        // grimsi bir ton (#959b9e) çıkıyordu. Seçim marka amber'i olmalı.
        Table: {
            rowSelectedBg: '#F5DEB9',        // = --accent-row
            rowSelectedHoverBg: '#EFD3A8',
        },
    },
};

/**
 * Koyu mod: darkAlgorithm + compact. Nötrler brand.css'teki :root[data-theme="dark"] ile aynı.
 * Metin rengi (colorText) verilmez — darkAlgorithm'in açık tonu kullanılır.
 */
const antdDarkTheme = {
    algorithm: [theme.darkAlgorithm, theme.compactAlgorithm],
    token: {
        // Slate birincil koyu zeminde kayboluyordu → aynı ailenin açık tonu (buton, link, odak halkası).
        colorPrimary: '#56738F',
        colorLink: '#8FB0CF',
        colorLinkHover: '#A9C4DF',
        colorWarning: '#E0982A',
        // Seçili item zemini: açık temadaki amber'in koyu tonu.
        controlItemBgActive: '#4A3B22',
        controlItemBgActiveHover: '#57462A',
        colorBgLayout: '#12171D',            // = --bg
        colorBgContainer: '#1A2129',         // = --surface
        colorBgElevated: '#212A33',
        colorBorder: '#33404D',              // = --border
        colorBorderSecondary: '#29333D',
        borderRadius: 6,
        fontFamily: FONT_FAMILY,
    },
    components: {
        // İşaretle-seç satır zemini (checkbox): koyu temada açık amber kalırsa üstündeki
        // açık metin (hasta adı vb.) okunmaz oluyordu — koyu temanın amber'i kullanılır.
        // Değerler brand.css'teki :root[data-theme="dark"] --accent-row ile aynı.
        Table: {
            rowSelectedBg: '#6B4816',        // = koyu tema --accent-row
            rowSelectedHoverBg: '#7B551C',
        },
    },
};

export const getAntdTheme = (mode) => (mode === 'dark' ? antdDarkTheme : antdTheme);

/**
 * Statik çağrılar — Modal.confirm/Modal.info/Modal.warning, message.*, notification.* — React
 * ağacının DIŞINDA, kendi köklerinde render edilir. Bu yüzden ConfigProvider'ı göremezler ve
 * antd'nin fabrika temasına düşerler (v5'in bilinen davranışı: "static function can not consume
 * context"). Onay modallarındaki birincil butonun mavi çıkmasının sebebi buydu — tema değil, kök farkı.
 *
 * holderRender o kökleri de aynı temaya sarar; onlarca çağrı yerini App.useApp()'e taşımak gerekmez.
 * Mod her çağrıda okunur: tema değiştirildikten sonra açılan onay modalı da doğru temada çıkar.
 * Uygulama açılışında bir kez çağrılır (index.js).
 */
export function applyAntdThemeToStaticApis() {
    ConfigProvider.config({
        holderRender: (children) => (
            <ConfigProvider theme={getAntdTheme(getThemeMode())}>{children}</ConfigProvider>
        ),
    });
}
